import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import api, { subscriptionApi, documentApi, subscriptionPlanApi, profileApi, boardOverageApi, BoardOverageStatus, cardOverageApi, CARDS_PER_TRANCHE, storageOverageApi, StorageOverageStatus } from '../services/api';
import { Check, Xmark, Download, Trash, User, Lock, Camera, LogOut, Code, HeadsetHelp, ViewGrid, CreditCard, StatsUpSquare, Page } from 'iconoir-react';
import { useAuth } from '../contexts/AuthContext';
import { ConfirmModal } from '../components/ConfirmModal';
import ApiTokensManagement from '../components/ApiTokensManagement';
import { toDisplayNumber, formatStorageMb, getPlanPricing, getPlanDisplayFeatures } from '../utils/planPricing';
import '../styles/account-management.css';
import '../styles/support.css';

interface Subscription {
  id: number;
  plan: any;
  billing_period: 'monthly' | 'yearly';
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancelled_at: string | null;
  is_active: boolean;
}

interface Document {
  id: number;
  filename: string;
  mime_type: string;
  size: number;
  formatted_size: string;
  file_type: string;
  created_at: string;
  board_name: string;
  card_title: string;
  path: string;
}

interface Usage {
  boards_count: number;
  cards_count: number;
  storage_gb: number;
}

interface Overage {
  limit: number;
  used: number;
  overage: number;
  price_per_unit: number;
  unit_label: string;
}

type TabType = 'profile' | 'subscription' | 'overages' | 'documents' | 'api';

export const AccountManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>(
    location.state?.activeTab || (searchParams.get('tab') as TabType) || 'profile'
  );
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [overages, setOverages] = useState<any>(null);
  const [boardOverageStatus, setBoardOverageStatus] = useState<BoardOverageStatus | null>(null);
  const [storageOverageStatus, setStorageOverageStatus] = useState<StorageOverageStatus | null>(null);
  const [overagePaymentMessage, setOveragePaymentMessage] = useState<string | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentStats, setDocumentStats] = useState<any>(null);
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [sortBy, setSortBy] = useState<'created_at' | 'original_filename' | 'size' | 'mime_type'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterType, setFilterType] = useState<string>('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Profile states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [avatarError, setAvatarError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [avatarSuccess, setAvatarSuccess] = useState('');
  const [showDeleteAvatarConfirm, setShowDeleteAvatarConfirm] = useState(false);
  const [planChangeRequest, setPlanChangeRequest] = useState<{ planId: number; billingPeriod: 'monthly' | 'yearly' } | null>(null);
  const [paymentRequiredPlan, setPaymentRequiredPlan] = useState<{ planId: number; billingPeriod: string } | null>(null);
  const [showCancelSubscriptionConfirm, setShowCancelSubscriptionConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<number | null>(null);
  const [showDeleteMultipleConfirm, setShowDeleteMultipleConfirm] = useState(false);

  useEffect(() => {
    loadData();
    if (user) {
      setName(user.name);
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'documents') {
      loadDocuments();
    }
  }, [activeTab, sortBy, sortOrder, filterType]);

  // Retour de paiement Stancer pour un dépassement tableaux OU cartes
  // (returnUrl = /account?tab=overages&payment_id=...). Le type (board/card) est
  // mémorisé avant redirection car le payment_id seul ne l'indique pas au retour.
  useEffect(() => {
    const paymentId = searchParams.get('payment_id');
    if (!paymentId || activeTab !== 'overages') return;

    const confirmOveragePayment = async () => {
      const kind = sessionStorage.getItem('overage_payment_kind') || 'board';
      try {
        if (kind === 'card') {
          const response = await cardOverageApi.confirm(paymentId);
          setOveragePaymentMessage(
            response.data.processed
              ? 'Paiement confirmé : votre dépassement de cartes est actif.'
              : "Paiement en cours de vérification. Rafraîchissez la page dans quelques instants si le statut ne se met pas à jour."
          );
        } else if (kind === 'storage') {
          const response = await storageOverageApi.confirm(paymentId);
          setOveragePaymentMessage(
            response.data.processed
              ? 'Paiement confirmé : votre stockage supplémentaire est actif.'
              : "Paiement en cours de vérification. Rafraîchissez la page dans quelques instants si le statut ne se met pas à jour."
          );
        } else {
          const response = await boardOverageApi.confirm(paymentId);
          setOveragePaymentMessage(
            response.data.processed
              ? 'Paiement confirmé : votre dépassement de tableaux est actif.'
              : "Paiement en cours de vérification. Rafraîchissez la page dans quelques instants si le statut ne se met pas à jour."
          );
        }
        await loadBoardOverageStatus();
        await loadStorageOverageStatus();
        await loadUsageAndOverages();
      } catch (error) {
        console.error('Erreur lors de la confirmation du paiement de dépassement:', error);
        setOveragePaymentMessage('Erreur lors de la vérification du paiement. Contactez le support si le prélèvement a bien eu lieu.');
      } finally {
        sessionStorage.removeItem('overage_payment_kind');
        // Nettoie l'URL pour ne pas reconfirmer le paiement au prochain rafraîchissement.
        searchParams.delete('payment_id');
        setSearchParams(searchParams, { replace: true });
      }
    };

    confirmOveragePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subResponse, plansResponse] = await Promise.all([
        subscriptionApi.getCurrent(),
        subscriptionPlanApi.getAll()
      ]);

      setSubscription(subResponse.data.subscription);
      setAvailablePlans(plansResponse.data.plans);

      if (activeTab === 'overages') {
        await loadUsageAndOverages();
        await loadBoardOverageStatus();
        await loadStorageOverageStatus();
      } else if (activeTab === 'documents') {
        await loadUsageAndOverages();
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBoardOverageStatus = async () => {
    try {
      const response = await boardOverageApi.getStatus();
      setBoardOverageStatus(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du statut de dépassement tableaux:', error);
    }
  };

  const loadStorageOverageStatus = async () => {
    try {
      const response = await storageOverageApi.getStatus();
      setStorageOverageStatus(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement du statut de dépassement stockage:', error);
    }
  };

  // La périodicité est déterminée par l'abonnement actif côté backend.
  const handlePurchaseBoardOverage = async (tranches: number) => {
    try {
      const response = await boardOverageApi.checkout({ tranches });
      sessionStorage.setItem('overage_payment_kind', 'board');
      window.location.href = response.data.payment_page_url;
    } catch (error: any) {
      alert(error.response?.data?.message || "Erreur lors de la création du paiement de dépassement");
    }
  };

  const handlePurchaseCardOverage = async (tranches: number) => {
    try {
      const response = await cardOverageApi.checkout({ tranches });
      sessionStorage.setItem('overage_payment_kind', 'card');
      window.location.href = response.data.payment_page_url;
    } catch (error: any) {
      alert(error.response?.data?.message || "Erreur lors de la création du paiement de dépassement");
    }
  };

  const handlePurchaseStorageOverage = async (gb: number) => {
    try {
      const response = await storageOverageApi.checkout({ gb });
      sessionStorage.setItem('overage_payment_kind', 'storage');
      window.location.href = response.data.payment_page_url;
    } catch (error: any) {
      alert(error.response?.data?.message || "Erreur lors de la création du paiement de dépassement");
    }
  };

  const loadUsageAndOverages = async () => {
    try {
      const response = await subscriptionApi.getUsage();
      setUsage(response.data.usage);
      setOverages(response.data.overages);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisation:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await documentApi.getAll({ sort_by: sortBy, sort_order: sortOrder, filter_type: filterType });
      setDocuments(response.data.documents);
      setDocumentStats(response.data.stats);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
    }
  };

  const handleChangePlan = (planId: number, billingPeriod: 'monthly' | 'yearly') => {
    setPlanChangeRequest({ planId, billingPeriod });
  };

  const handleConfirmChangePlan = async () => {
    const request = planChangeRequest;
    setPlanChangeRequest(null);
    if (!request) return;

    try {
      const response = await subscriptionApi.changePlan(request.planId, request.billingPeriod);
      alert(response.data.message);
      await loadData();
    } catch (error: any) {
      // Si le code est 402 (Payment Required), proposer le paiement
      if (error.response?.status === 402) {
        const { plan, billing_period } = error.response.data;
        setPaymentRequiredPlan({ planId: plan.id, billingPeriod: billing_period });
      } else {
        alert(error.response?.data?.message || 'Erreur lors du changement de plan');
      }
    }
  };

  const handleConfirmOveragePayment = () => {
    const request = paymentRequiredPlan;
    setPaymentRequiredPlan(null);
    if (!request) return;

    navigate('/upgrade-plan', {
      state: {
        planId: request.planId,
        billingPeriod: request.billingPeriod
      }
    });
  };

  const handleCancelSubscription = () => {
    setShowCancelSubscriptionConfirm(true);
  };

  const handleConfirmCancelSubscription = async () => {
    setShowCancelSubscriptionConfirm(false);

    try {
      const response = await subscriptionApi.cancel();
      alert(response.data.message);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de l\'annulation');
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      const response = await subscriptionApi.reactivate();
      alert(response.data.message);
      await loadData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de la réactivation');
    }
  };

  const handleDeleteDocument = (id: number) => {
    setDocumentToDelete(id);
  };

  const handleConfirmDeleteDocument = async () => {
    const id = documentToDelete;
    setDocumentToDelete(null);
    if (id === null) return;

    try {
      await documentApi.delete(id);
      await loadDocuments();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteMultiple = () => {
    if (selectedDocuments.length === 0) {
      alert('Aucun document sélectionné');
      return;
    }

    setShowDeleteMultipleConfirm(true);
  };

  const handleConfirmDeleteMultiple = async () => {
    setShowDeleteMultipleConfirm(false);

    try {
      const response = await documentApi.deleteMultiple(selectedDocuments);

      alert(response.data.message);

      setSelectedDocuments([]);
      await loadDocuments();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const toggleDocumentSelection = (id: number) => {
    setSelectedDocuments(prev =>
      prev.includes(id) ? prev.filter(docId => docId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDocuments.length === documents.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(documents.map(doc => doc.id));
    }
  };

  // Profile handlers
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setIsUpdatingProfile(true);

    try {
      // C01 — Sanctum SPA : api (withCredentials) au lieu d'axios + Bearer
      await api.put('/profile', { name, email });

      setProfileSuccess('Profil mis à jour avec succès !');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (err: any) {
      setProfileError(err.response?.data?.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      await api.put('/profile/password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });

      setPasswordSuccess('Mot de passe mis à jour avec succès !');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (err: any) {
      setPasswordError(err.response?.data?.message || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      setAvatarError('L\'image ne doit pas dépasser 2 Mo');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setAvatarError('Le fichier doit être une image');
      return;
    }

    setAvatarFile(file);
    setAvatarError('');

    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const refreshUserData = async () => {
    try {
      await api.get('/me');
      window.location.reload();
    } catch (err) {
      console.error('Erreur lors du rechargement des données utilisateur', err);
    }
  };

  const handleUploadAvatar = async () => {
    if (!avatarFile) return;

    setIsUploadingAvatar(true);
    setAvatarError('');
    setAvatarSuccess('');

    try {
      await profileApi.uploadAvatar(avatarFile);
      setAvatarSuccess('Avatar mis à jour avec succès !');
      setAvatarFile(null);
      setAvatarPreview(null);
      
      await refreshUserData();
      
      setTimeout(() => setAvatarSuccess(''), 3000);
    } catch (err: any) {
      setAvatarError(err.response?.data?.message || 'Erreur lors de l\'upload de l\'avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = () => {
    setShowDeleteAvatarConfirm(true);
  };

  const handleConfirmDeleteAvatar = async () => {
    setShowDeleteAvatarConfirm(false);
    setIsUploadingAvatar(true);
    setAvatarError('');

    try {
      await profileApi.deleteAvatar();
      setAvatarSuccess('Avatar supprimé avec succès !');

      await refreshUserData();

      setTimeout(() => setAvatarSuccess(''), 3000);
    } catch (err: any) {
      setAvatarError(err.response?.data?.message || 'Erreur lors de la suppression de l\'avatar');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleLogout = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
  };

  if (loading) {
    return (
      <div className="account-management-page">
        <div className="loading">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="account-management-page">
      <div className="account-header">
        <h1>Gestion de mon compte</h1>
        <div className="account-header-actions">
          <button className="btn btn-secondary" onClick={() => navigate('/support')}>
            <HeadsetHelp width={18} height={18} strokeWidth={2} />
            Support
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            <ViewGrid width={18} height={18} strokeWidth={2} />
            Retour au dashboard
          </button>
          <button className="btn btn-logout" onClick={handleLogout} title="Déconnexion">
            <LogOut width={18} height={18} strokeWidth={2} />
            Se déconnecter
          </button>
        </div>
      </div>

      <div className="account-tabs">
        <button
          className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <User width={20} height={20} strokeWidth={2} />
          Profil
        </button>
        <button
          className={`tab ${activeTab === 'subscription' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscription')}
        >
          <CreditCard width={20} height={20} strokeWidth={2} />
          Abonnement
        </button>
        <button
          className={`tab ${activeTab === 'overages' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('overages');
            loadUsageAndOverages();
            loadBoardOverageStatus();
            loadStorageOverageStatus();
          }}
        >
          <StatsUpSquare width={20} height={20} strokeWidth={2} />
          Dépassements
        </button>
        <button
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('documents');
            loadUsageAndOverages();
          }}
        >
          <Page width={20} height={20} strokeWidth={2} />
          Documents
        </button>
        <button
          className={`tab ${activeTab === 'api' ? 'active' : ''}`}
          onClick={() => setActiveTab('api')}
        >
          <Code width={20} height={20} strokeWidth={2} />
          API
        </button>
      </div>

      <div className="account-content">
        {activeTab === 'profile' && user && (
          <ProfileTab
            user={user}
            name={name}
            email={email}
            currentPassword={currentPassword}
            newPassword={newPassword}
            confirmPassword={confirmPassword}
            avatarFile={avatarFile}
            avatarPreview={avatarPreview}
            isUpdatingProfile={isUpdatingProfile}
            isUpdatingPassword={isUpdatingPassword}
            isUploadingAvatar={isUploadingAvatar}
            profileError={profileError}
            passwordError={passwordError}
            avatarError={avatarError}
            profileSuccess={profileSuccess}
            passwordSuccess={passwordSuccess}
            avatarSuccess={avatarSuccess}
            onNameChange={setName}
            onEmailChange={setEmail}
            onCurrentPasswordChange={setCurrentPassword}
            onNewPasswordChange={setNewPassword}
            onConfirmPasswordChange={setConfirmPassword}
            onAvatarChange={handleAvatarChange}
            onUpdateProfile={handleUpdateProfile}
            onUpdatePassword={handleUpdatePassword}
            onUploadAvatar={handleUploadAvatar}
            onDeleteAvatar={handleDeleteAvatar}
          />
        )}

        {activeTab === 'subscription' && subscription && (
          <SubscriptionTab
            subscription={subscription}
            availablePlans={availablePlans}
            onChangePlan={handleChangePlan}
            onCancel={handleCancelSubscription}
            onReactivate={handleReactivateSubscription}
          />
        )}

        {activeTab === 'overages' && usage && overages && (
          <OveragesTab
            usage={usage}
            overages={overages}
            plan={subscription?.plan}
            boardOverageStatus={boardOverageStatus}
            storageOverageStatus={storageOverageStatus}
            paymentMessage={overagePaymentMessage}
            onPurchaseBoardOverage={handlePurchaseBoardOverage}
            onPurchaseCardOverage={handlePurchaseCardOverage}
            onPurchaseStorageOverage={handlePurchaseStorageOverage}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab
            documents={documents}
            stats={documentStats}
            storageOverage={overages?.storage}
            selectedDocuments={selectedDocuments}
            sortBy={sortBy}
            sortOrder={sortOrder}
            filterType={filterType}
            onSortChange={(field) => {
              if (sortBy === field) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              } else {
                setSortBy(field);
                setSortOrder('desc');
              }
            }}
            onFilterChange={setFilterType}
            onToggleSelection={toggleDocumentSelection}
            onToggleSelectAll={toggleSelectAll}
            onDelete={handleDeleteDocument}
            onDeleteMultiple={handleDeleteMultiple}
            onGoToOverages={() => {
              setActiveTab('overages');
              loadUsageAndOverages();
              loadStorageOverageStatus();
            }}
          />
        )}

        {activeTab === 'api' && <ApiTab />}
      </div>
      <ConfirmModal
        isOpen={showDeleteAvatarConfirm}
        title="Supprimer l'avatar"
        message="Voulez-vous vraiment supprimer votre avatar ?"
        confirmText="Supprimer"
        onConfirm={handleConfirmDeleteAvatar}
        onCancel={() => setShowDeleteAvatarConfirm(false)}
        variant="danger"
      />

      <ConfirmModal
        isOpen={planChangeRequest !== null}
        title="Changer de plan"
        message="Êtes-vous sûr de vouloir changer de plan ?"
        confirmText="Changer de plan"
        onConfirm={handleConfirmChangePlan}
        onCancel={() => setPlanChangeRequest(null)}
      />

      <ConfirmModal
        isOpen={paymentRequiredPlan !== null}
        title="Paiement requis"
        message="Un paiement est requis pour ce plan. Souhaitez-vous procéder au paiement ?"
        confirmText="Procéder au paiement"
        onConfirm={handleConfirmOveragePayment}
        onCancel={() => setPaymentRequiredPlan(null)}
        variant="warning"
      />

      <ConfirmModal
        isOpen={showCancelSubscriptionConfirm}
        title="Annuler l'abonnement"
        message="Êtes-vous sûr de vouloir annuler votre abonnement ?"
        confirmText="Annuler l'abonnement"
        onConfirm={handleConfirmCancelSubscription}
        onCancel={() => setShowCancelSubscriptionConfirm(false)}
        variant="danger"
      />

      <ConfirmModal
        isOpen={documentToDelete !== null}
        title="Supprimer le document"
        message="Êtes-vous sûr de vouloir supprimer ce document ?"
        confirmText="Supprimer"
        onConfirm={handleConfirmDeleteDocument}
        onCancel={() => setDocumentToDelete(null)}
        variant="danger"
      />

      <ConfirmModal
        isOpen={showDeleteMultipleConfirm}
        title="Supprimer les documents"
        message={`Êtes-vous sûr de vouloir supprimer ${selectedDocuments.length} document(s) ?`}
        confirmText="Supprimer"
        onConfirm={handleConfirmDeleteMultiple}
        onCancel={() => setShowDeleteMultipleConfirm(false)}
        variant="danger"
      />

      <ConfirmModal
        isOpen={showLogoutConfirm}
        title="Déconnexion"
        message="Voulez-vous vraiment vous déconnecter ?"
        confirmText="Se déconnecter"
        onConfirm={handleConfirmLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </div>
  );
};

// Profile Tab Component
interface ProfileTabProps {
  user: any;
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  avatarFile: File | null;
  avatarPreview: string | null;
  isUpdatingProfile: boolean;
  isUpdatingPassword: boolean;
  isUploadingAvatar: boolean;
  profileError: string;
  passwordError: string;
  avatarError: string;
  profileSuccess: string;
  passwordSuccess: string;
  avatarSuccess: string;
  onNameChange: (name: string) => void;
  onEmailChange: (email: string) => void;
  onCurrentPasswordChange: (password: string) => void;
  onNewPasswordChange: (password: string) => void;
  onConfirmPasswordChange: (password: string) => void;
  onAvatarChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUpdateProfile: (e: React.FormEvent) => void;
  onUpdatePassword: (e: React.FormEvent) => void;
  onUploadAvatar: () => void;
  onDeleteAvatar: () => void;
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  user,
  name,
  email,
  currentPassword,
  newPassword,
  confirmPassword,
  avatarFile,
  avatarPreview,
  isUpdatingProfile,
  isUpdatingPassword,
  isUploadingAvatar,
  profileError,
  passwordError,
  avatarError,
  profileSuccess,
  passwordSuccess,
  avatarSuccess,
  onNameChange,
  onEmailChange,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onAvatarChange,
  onUpdateProfile,
  onUpdatePassword,
  onUploadAvatar,
  onDeleteAvatar
}) => {
  return (
    <div className="profile-tab">
      <h2>Informations personnelles</h2>

      {/* Avatar Section */}
      <div className="avatar-section">
        <div className="avatar-preview">
          {avatarPreview ? (
            <img src={avatarPreview} alt="Aperçu avatar" className="avatar-image" />
          ) : user.avatar_url ? (
            <img src={user.avatar_url} alt="Avatar actuel" className="avatar-image" />
          ) : (
            <div className="avatar-placeholder">
              {user.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        <div className="avatar-actions">
          <label htmlFor="avatar-upload" className="btn btn-secondary btn-sm">
            <Camera width={16} height={16} />
            Choisir une image
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={onAvatarChange}
            style={{ display: 'none' }}
          />
          
          {avatarFile && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onUploadAvatar}
              disabled={isUploadingAvatar}
            >
              <Check width={16} height={16} />
              {isUploadingAvatar ? 'Upload...' : 'Enregistrer'}
            </button>
          )}
          
          {user.avatar_url && !avatarFile && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={onDeleteAvatar}
              disabled={isUploadingAvatar}
            >
              <Trash width={16} height={16} />
              Supprimer
            </button>
          )}
        </div>
        
        <div className="avatar-info">
          <small>Format: JPG, PNG, GIF - Taille max: 2 Mo</small>
        </div>
        
        {avatarError && <div className="settings-error">{avatarError}</div>}
        {avatarSuccess && (
          <div className="settings-success">
            <Check width={18} height={18} />
            {avatarSuccess}
          </div>
        )}
      </div>

      {/* Profile Form */}
      <div className="profile-form-section">
        <h3>Informations du compte</h3>
        {profileError && <div className="settings-error">{profileError}</div>}
        {profileSuccess && (
          <div className="settings-success">
            <Check width={18} height={18} />
            {profileSuccess}
          </div>
        )}

        <form onSubmit={onUpdateProfile} className="settings-form">
          <div className="form-group">
            <label htmlFor="name">Nom complet</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              required
              disabled={isUpdatingProfile}
              placeholder="Votre nom"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              required
              disabled={isUpdatingProfile}
              placeholder="votre@email.com"
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isUpdatingProfile}
          >
            {isUpdatingProfile ? 'Mise à jour...' : 'Mettre à jour le profil'}
          </button>
        </form>
      </div>

      {/* Password Form */}
      <div className="password-form-section">
        <h3>Changer le mot de passe</h3>
        {passwordError && <div className="settings-error">{passwordError}</div>}
        {passwordSuccess && (
          <div className="settings-success">
            <Check width={18} height={18} />
            {passwordSuccess}
          </div>
        )}

        <form onSubmit={onUpdatePassword} className="settings-form">
          <div className="form-group">
            <label htmlFor="current-password">Mot de passe actuel</label>
            <input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => onCurrentPasswordChange(e.target.value)}
              required
              disabled={isUpdatingPassword}
              placeholder="••••••••"
            />
          </div>

          <div className="form-group">
            <label htmlFor="new-password">Nouveau mot de passe</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => onNewPasswordChange(e.target.value)}
              required
              disabled={isUpdatingPassword}
              placeholder="••••••••"
              minLength={8}
            />
            <small>Au moins 8 caractères</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirmer le nouveau mot de passe</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => onConfirmPasswordChange(e.target.value)}
              required
              disabled={isUpdatingPassword}
              placeholder="••••••••"
            />
          </div>

          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={isUpdatingPassword}
          >
            {isUpdatingPassword ? 'Mise à jour...' : 'Changer le mot de passe'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Subscription Tab Component
export interface SubscriptionTabProps {
  subscription: Subscription;
  availablePlans: any[];
  onChangePlan: (planId: number, billingPeriod: 'monthly' | 'yearly') => void;
  onCancel: () => void;
  onReactivate: () => void;
}

export const SubscriptionTab: React.FC<SubscriptionTabProps> = ({
  subscription,
  availablePlans,
  onChangePlan,
  onCancel,
  onReactivate
}) => {
  return (
    <div className="subscription-tab">
      <div className="current-subscription">
        <h2>Abonnement actuel</h2>
        <div className="subscription-card">
          <div className="subscription-info">
            <h3>{subscription.plan.display_name}</h3>
            <p className="subscription-status">
              Statut: <span className={`status-badge ${subscription.status}`}>{subscription.status}</span>
            </p>
            <p>Période: {subscription.billing_period === 'monthly' ? 'Mensuel' : 'Annuel'}</p>
            <p>Début: {new Date(subscription.current_period_start).toLocaleDateString()}</p>
            <p>Fin: {new Date(subscription.current_period_end).toLocaleDateString()}</p>
            {subscription.cancelled_at && (
              <p className="cancelled">Annulé le: {new Date(subscription.cancelled_at).toLocaleDateString()}</p>
            )}
          </div>
          <div className="subscription-actions">
            {/* Plan gratuit : pas d'annulation/réactivation (toujours actif).
                Pour quitter Ventilio, l'utilisateur supprime son compte. */}
            {!subscription.plan.has_ads && (
              subscription.cancelled_at ? (
                <button className="btn btn-primary" onClick={onReactivate}>
                  Réactiver l'abonnement
                </button>
              ) : (
                <button className="btn btn-danger" onClick={onCancel}>
                  Annuler l'abonnement
                </button>
              )
            )}
          </div>
        </div>
      </div>

      <div className="available-plans">
        <h2>Changer de plan</h2>

        <div className="plans-grid">
          {availablePlans.map(plan => {
            const pricing = getPlanPricing(plan);
            const isCurrent = plan.id === subscription.plan.id;
            const displayFeatures = getPlanDisplayFeatures(plan);
            const selectThisPlan = () => onChangePlan(plan.id, pricing.billingPeriod);

            const cardInteractiveProps = isCurrent
              ? {}
              : {
                  role: 'button' as const,
                  tabIndex: 0,
                  'aria-label': `Choisir le plan ${plan.display_name}`,
                  onClick: selectThisPlan,
                  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      selectThisPlan();
                    }
                  },
                };

            return (
              <div
                key={plan.id}
                className={`plan-card ${isCurrent ? 'current' : 'plan-card-selectable'}`}
                {...cardInteractiveProps}
              >
                <h3>{plan.display_name}</h3>
                <p className="plan-description">{plan.description}</p>
                <div className="plan-price">
                  <span className="price">{pricing.amount.toFixed(2)}€</span>
                  {pricing.suffix && <span className="period">{pricing.suffix}</span>}
                </div>
                <ul className="plan-features">
                  {displayFeatures.map((feature: string, index: number) => (
                    <li key={index}>
                      <Check width={16} height={16} /> {feature}
                    </li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button className="btn btn-secondary" disabled>
                    Plan actuel
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary"
                    tabIndex={-1}
                    onClick={(e) => {
                      e.stopPropagation();
                      selectThisPlan();
                    }}
                  >
                    Choisir ce plan
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Arrondit au multiple de CARDS_PER_TRANCHE le plus proche (mini 1 tranche).
const snapToCardsTrancheMultiple = (value: number): number => {
  if (Number.isNaN(value) || value < CARDS_PER_TRANCHE) return CARDS_PER_TRANCHE;
  return Math.round(value / CARDS_PER_TRANCHE) * CARDS_PER_TRANCHE;
};

// Overages Tab Component
interface OveragesTabProps {
  usage: Usage;
  overages: any;
  plan: any;
  boardOverageStatus?: import('../services/api').BoardOverageStatus | null;
  storageOverageStatus?: import('../services/api').StorageOverageStatus | null;
  paymentMessage?: string | null;
  onPurchaseBoardOverage?: (tranches: number) => void;
  onPurchaseCardOverage?: (tranches: number) => void;
  onPurchaseStorageOverage?: (gb: number) => void;
}

const OveragesTab: React.FC<OveragesTabProps> = ({
  usage,
  overages,
  plan,
  boardOverageStatus,
  storageOverageStatus,
  paymentMessage,
  onPurchaseBoardOverage,
  onPurchaseCardOverage,
  onPurchaseStorageOverage,
}) => {
  const isFreePlan = plan?.has_ads === true;
  const boardsPerTranche = boardOverageStatus?.boards_per_tranche ?? 10;
  // "Tableaux supplémentaires" uniquement à l'écran ; la conversion en tranches
  // pour le backend reste interne à ce composant (cf. onPurchaseBoardOverage).
  const [overageBoards, setOverageBoards] = useState(boardsPerTranche);
  const [overageStorageGb, setOverageStorageGb] = useState(1);
  const [overageCards, setOverageCards] = useState(CARDS_PER_TRANCHE);

  const billingPeriod: 'monthly' | 'yearly' = overages.billing_period === 'yearly' ? 'yearly' : 'monthly';
  const periodSuffix = billingPeriod === 'yearly' ? '/ an' : '/ mois';
  const periodLabel = billingPeriod === 'yearly' ? 'annuelle' : 'mensuelle';
  const activePrice = (monthly: unknown, yearly: unknown): number =>
    toDisplayNumber(billingPeriod === 'yearly' ? yearly : monthly);

  // Arrondit au multiple de boardsPerTranche le plus proche (mini 1 tranche).
  const snapToTrancheMultiple = (value: number): number => {
    const rounded = Math.round(value / boardsPerTranche) * boardsPerTranche;
    return Math.max(boardsPerTranche, rounded);
  };

  const overageTranches = overageBoards / boardsPerTranche;
  const pricePerTranche = activePrice(
    boardOverageStatus?.pricing.price_per_tranche_monthly || 0,
    boardOverageStatus?.pricing.price_per_tranche_yearly || 0
  );
  const estimatedCost = overageTranches * pricePerTranche;

  const overageCardsTranches = overageCards / CARDS_PER_TRANCHE;
  const cardsPricePerTranche = activePrice(overages.cards.price_per_unit, overages.cards.price_per_unit_yearly);
  const cardsEstimatedCost = overageCardsTranches * cardsPricePerTranche;

  return (
    <div className="overages-tab">
      <h2>
        {isFreePlan ? 'Utilisation et limites' : 'Utilisation et dépassements'}
      </h2>

      {paymentMessage && (
        <p className="overage-info" style={{ padding: '10px 14px', marginBottom: '16px' }}>
          {paymentMessage}
        </p>
      )}

      <div className="usage-cards">
        {/* TABLEAUX */}
        <div className="usage-card">
          <h3>Tableaux</h3>

          <div className="usage-bar">
            <div
              className="usage-fill"
              style={{
                width: `${Math.min(
                  (usage.boards_count / overages.boards.limit) * 100,
                  100
                )}%`
              }}
            />
          </div>

          <p>
            {usage.boards_count} / {overages.boards.limit} inclus
            {overages.boards.effective_limit != null && overages.boards.effective_limit !== overages.boards.limit && (
              <> ({overages.boards.effective_limit} avec dépassement actif)</>
            )}
          </p>

          {overages.boards.active_overage_quantity > 0 && (
            <p className="overage-info">
              Dépassement actif : +{overages.boards.active_overage_quantity} tableau(x)
              {overages.boards.active_overage_expires_at && (
                <> jusqu'au {new Date(overages.boards.active_overage_expires_at).toLocaleDateString('fr-FR')}</>
              )}
            </p>
          )}

          {overages.boards.overage > 0 && (
            <p className="overage-warning">
              Dépassement à régler : {overages.boards.overage} tableau(x)

              {!isFreePlan && (
                <>
                  <br />
                  Coût : {(
                    Math.ceil(overages.boards.overage / 10) *
                    activePrice(overages.boards.price_per_unit, overages.boards.price_per_unit_yearly)
                  ).toFixed(2)}€
                </>
              )}
            </p>
          )}

          {isFreePlan ? (
            <p className="overage-info">
              Aucun dépassement payant n'est possible avec l'offre gratuite.
            </p>
          ) : boardOverageStatus?.can_purchase && onPurchaseBoardOverage ? (
            <div className="board-overage-purchase-form">
              <h4>Acheter un dépassement de tableaux</h4>
              <div className="board-overage-purchase-row">
                <label>
                  Tableaux supplémentaires
                  <input
                    type="number"
                    min={boardsPerTranche}
                    max={1000}
                    step={boardsPerTranche}
                    value={overageBoards}
                    onChange={(e) => setOverageBoards(snapToTrancheMultiple(parseInt(e.target.value, 10) || boardsPerTranche))}
                  />
                </label>
              </div>
              <p>
                Prix par tranche : <strong>{pricePerTranche.toFixed(2)}€ {periodSuffix}</strong>
              </p>
              <p>
                Coût estimé : <strong>{estimatedCost.toFixed(2)}€</strong>
              </p>
              <button
                className="btn btn-primary"
                onClick={() => onPurchaseBoardOverage(overageTranches)}
              >
                Payer le dépassement
              </button>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                Achat manuel, non reconduit automatiquement et jamais ajouté à la
                facturation de votre abonnement.
              </p>
            </div>
          ) : null}
        </div>

        <div className="usage-card">
          <h3>Cartes</h3>

          <div className="usage-bar">
            <div
              className="usage-fill"
              style={{
                width: `${overages.cards.effective_limit != null
                  ? Math.min((overages.cards.total_used / overages.cards.effective_limit) * 100, 100)
                  : 0}%`
              }}
            />
          </div>

          <p>
            Cartes utilisées : {overages.cards.total_used} / {overages.cards.effective_limit ?? 'illimité'}
            {overages.cards.effective_limit != null && overages.cards.effective_limit !== overages.cards.limit && (
              <> ({overages.cards.limit} inclus)</>
            )}
          </p>

          {overages.cards.percentage >= 90 && overages.cards.percentage < 100 && (
            <p className="overage-warning">
              Vous approchez de votre limite de cartes ({overages.cards.percentage}%).
            </p>
          )}

          {overages.cards.active_overage_quantity > 0 && (
            <p className="overage-info">
              Dépassement actif : +{overages.cards.active_overage_quantity} carte(s)
              {overages.cards.active_overage_expires_at && (
                <> jusqu'au {new Date(overages.cards.active_overage_expires_at).toLocaleDateString('fr-FR')}</>
              )}
            </p>
          )}

          {overages.cards.effective_limit != null && overages.cards.total_used >= overages.cards.effective_limit && (
            <p className="overage-warning">
              Limite de cartes atteinte.
              <br />
              {isFreePlan
                ? 'Passez à une offre Premium pour créer davantage de cartes.'
                : 'Achetez un dépassement de cartes pour continuer à en créer.'}
            </p>
          )}

          {isFreePlan ? (
            <p className="overage-info">
              Aucun dépassement payant n'est possible avec l'offre gratuite.
            </p>
          ) : overages.cards.can_purchase && onPurchaseCardOverage ? (
            <div className="board-overage-purchase-form">
              <h4>Acheter un dépassement de cartes</h4>
              <div className="board-overage-purchase-row">
                <label>
                  Cartes supplémentaires (par tranche de {CARDS_PER_TRANCHE})
                  <input
                    type="number"
                    min={CARDS_PER_TRANCHE}
                    max={CARDS_PER_TRANCHE * 100}
                    step={CARDS_PER_TRANCHE}
                    value={overageCards}
                    onChange={(e) => setOverageCards(snapToCardsTrancheMultiple(parseInt(e.target.value, 10) || CARDS_PER_TRANCHE))}
                  />
                </label>
              </div>
              <p>
                Soit {overageCardsTranches} tranche(s) = +{overageCardsTranches * CARDS_PER_TRANCHE} cartes
              </p>
              <p>
                Prix par tranche : <strong>{cardsPricePerTranche.toFixed(2)}€ {periodSuffix}</strong>
              </p>
              <p>
                Coût estimé : <strong>{cardsEstimatedCost.toFixed(2)}€</strong>
              </p>
              <button
                className="btn btn-primary"
                onClick={() => onPurchaseCardOverage(overageCardsTranches)}
              >
                Payer le dépassement
              </button>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                Achat manuel, non reconduit automatiquement et jamais ajouté à la
                facturation de votre abonnement.
              </p>
            </div>
          ) : null}

          {overages.cards.boards.length > 0 && (
            <details style={{ marginTop: '16px' }}>
              <summary>Répartition par tableau (information)</summary>
              <ul>
                {overages.cards.boards.map((board: any) => (
                  <li key={board.board_id}>
                    {board.board_title} : {board.used} carte(s)
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>

        {/* STOCKAGE */}
        <div className="usage-card">
          <h3>Stockage</h3>

          <div className="usage-bar">
            <div
              className="usage-fill"
              style={{
                width: `${overages.storage.effective_limit != null
                  ? Math.min((overages.storage.used / overages.storage.effective_limit) * 100, 100)
                  : 0}%`
              }}
            />
          </div>

          <p>
            {formatStorageMb(overages.storage.used)} / {overages.storage.effective_limit != null
              ? formatStorageMb(overages.storage.effective_limit)
              : 'illimité'}
            {overages.storage.effective_limit != null && overages.storage.effective_limit !== overages.storage.included_limit && (
              <> ({formatStorageMb(overages.storage.included_limit)} inclus)</>
            )}
          </p>

          {overages.storage.effective_limit != null &&
            overages.storage.used >= overages.storage.effective_limit &&
            overages.storage.overage <= 0 && (
              <p className="overage-warning">
                Limite de stockage atteinte.
                <br />
                Ajoutez du stockage supplémentaire pour pouvoir ajouter de nouveaux fichiers.
              </p>
            )
          }
          
          {overages.storage.overage > 0 && (
            <p className="overage-warning">
              Dépassement à régler : {formatStorageMb(overages.storage.overage)}

              {!isFreePlan && (
                <>
                  <br />
                  Coût : {(
                    Math.ceil(overages.storage.overage / 1024) *
                    activePrice(overages.storage.price_per_unit, overages.storage.price_per_unit_yearly)
                  ).toFixed(2)}€
                </>
              )}
            </p>
          )}

          {isFreePlan ? (
            <p className="overage-info">
              Aucun dépassement payant n'est possible avec l'offre gratuite.
            </p>
          ) : storageOverageStatus?.can_purchase && onPurchaseStorageOverage ? (
            <div className="board-overage-purchase-form">
              <h4>Ajouter du stockage supplémentaire</h4>
              <div className="board-overage-purchase-row">
                <label>
                  Stockage supplémentaire (Go)
                  <input
                    type="number"
                    min={1}
                    max={1000}
                    step={1}
                    value={overageStorageGb}
                    onChange={(e) => setOverageStorageGb(Math.max(1, parseInt(e.target.value, 10) || 1))}
                  />
                </label>
              </div>
              <p>
                Prix par Go : <strong>{activePrice(
                  storageOverageStatus.pricing.price_per_gb_monthly,
                  storageOverageStatus.pricing.price_per_gb_yearly
                ).toFixed(2)}€ {periodSuffix}</strong>
              </p>
              <p>
                Coût estimé : <strong>{(
                  overageStorageGb * activePrice(
                    storageOverageStatus.pricing.price_per_gb_monthly,
                    storageOverageStatus.pricing.price_per_gb_yearly
                  )
                ).toFixed(2)}€</strong>
              </p>
              <button
                className="btn btn-primary"
                onClick={() => onPurchaseStorageOverage(overageStorageGb)}
              >
                Payer le stockage supplémentaire
              </button>
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                Achat manuel, non reconduit automatiquement et jamais ajouté à la
                facturation de votre abonnement.
              </p>
            </div>
          ) : null}

          {!!storageOverageStatus?.active_overages?.length && (
            <div style={{ marginTop: '16px' }}>
              <h4>Stockage supplémentaire actif</h4>
              {storageOverageStatus.active_overages.map((o) => (
                <p key={o.id}>
                  +{o.gb} Go — {o.period === 'yearly' ? '1 an' : '1 mois'} — expire le{' '}
                  {new Date(o.expires_at).toLocaleDateString('fr-FR')}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Informations différentes selon le plan */}
      {isFreePlan ? (
        <div className="overage-info">
          <h3>Limites de votre offre gratuite</h3>

          <p>
            Les dépassements ne sont pas autorisés avec le plan gratuit.
            Lorsque vous atteignez une limite, la création ou l'ajout
            correspondant est bloqué.
          </p>

          <p>
            Passez à une offre Premium pour bénéficier de limites plus élevées
            et des dépassements payants.
          </p>
        </div>
      ) : (
        <div className="overage-info">
          <h3>Tarification des dépassements — offre Premium {periodLabel}</h3>

          <p>
            Votre abonnement Premium est {periodLabel === 'annuelle' ? 'annuel' : 'mensuel'}. Les
            dépassements sont facturés au tarif {periodLabel === 'annuelle' ? 'annuel' : 'mensuel'}.
          </p>

          <ul>
            <li>
              {activePrice(plan.price_per_10_extra_boards, plan.price_per_10_extra_boards_yearly).toFixed(2)}€ {periodSuffix} par tranche de 10 tableaux supplémentaires
            </li>
            <li>
              {activePrice(plan.price_per_1000_extra_cards, plan.price_per_1000_extra_cards_yearly).toFixed(2)}€ {periodSuffix} par tranche de 1000 cartes supplémentaires
            </li>
            <li>
              {activePrice(plan.price_per_extra_gb, plan.price_per_extra_gb_yearly).toFixed(2)}€ {periodSuffix} par Go de stockage supplémentaire
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

// Documents Tab Component
interface DocumentsTabProps {
  documents: Document[];
  stats: any;
  storageOverage?: { used: number; effective_limit: number | null; percentage: number } | null;
  selectedDocuments: number[];
  sortBy: string;
  sortOrder: string;
  filterType: string;
  onSortChange: (field: any) => void;
  onFilterChange: (type: string) => void;
  onToggleSelection: (id: number) => void;
  onToggleSelectAll: () => void;
  onDelete: (id: number) => void;
  onDeleteMultiple: () => void;
  onGoToOverages?: () => void;
}

const DocumentsTab: React.FC<DocumentsTabProps> = ({
  documents,
  stats,
  storageOverage,
  selectedDocuments,
  sortBy,
  sortOrder,
  filterType,
  onSortChange,
  onFilterChange,
  onToggleSelection,
  onToggleSelectAll,
  onDelete,
  onDeleteMultiple,
  onGoToOverages
}) => {
  const isNearCapacity = !!storageOverage && storageOverage.effective_limit != null && storageOverage.percentage >= 90;

  return (
    <div className="documents-tab">
      {isNearCapacity && storageOverage && (
        <div className="overage-warning" style={{ marginBottom: '16px' }}>
          {formatStorageMb(storageOverage.used)} / {formatStorageMb(storageOverage.effective_limit as number)} utilisés
          {' — '}{storageOverage.percentage.toFixed(0)}%
          <br />
          Votre espace de stockage est presque plein. Supprimez des fichiers ci-dessous
          ou{' '}
          <button
            className="btn btn-primary"
            style={{ marginLeft: '4px' }}
            onClick={onGoToOverages}
          >
            ajoutez du stockage
          </button>
        </div>
      )}

      <div className="documents-header">
        <h2>Mes documents</h2>
        {stats && (
          <div className="documents-stats">
            <span>{stats.total_count} documents</span>
            <span>{stats.total_size_formatted}</span>
          </div>
        )}
      </div>

      <div className="documents-controls">
        <div className="filter-controls">
          <select value={filterType} onChange={(e) => onFilterChange(e.target.value)}>
            <option value="">Tous les types</option>
            <option value="image">Images ({stats?.by_type.images || 0})</option>
            <option value="video">Vidéos ({stats?.by_type.videos || 0})</option>
            <option value="document">Documents ({stats?.by_type.documents || 0})</option>
            <option value="archive">Archives ({stats?.by_type.archives || 0})</option>
          </select>

          <select value={sortBy} onChange={(e) => onSortChange(e.target.value)}>
            <option value="created_at">Date d'ajout</option>
            <option value="original_filename">Nom</option>
            <option value="size">Taille</option>
            <option value="mime_type">Type</option>
          </select>

          <button
            className="btn btn-secondary"
            onClick={() => onSortChange(sortBy)}
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <div className="bulk-actions">
          {selectedDocuments.length > 0 && (
            <button className="btn btn-danger" onClick={onDeleteMultiple}>
              <Trash width={18} height={18} />
              Supprimer ({selectedDocuments.length})
            </button>
          )}
        </div>
      </div>

      <div className="account-documents-list">
        <div className="account-documents-list-header">
          <input
            type="checkbox"
            checked={selectedDocuments.length === documents.length && documents.length > 0}
            onChange={onToggleSelectAll}
          />
          <span>Nom</span>
          <span>Tableau</span>
          <span>Carte</span>
          <span>Taille</span>
          <span>Date</span>
          <span>Actions</span>
        </div>

        {documents.map(doc => (
          <div key={doc.id} className="account-document-row">
            <input
              type="checkbox"
              checked={selectedDocuments.includes(doc.id)}
              onChange={() => onToggleSelection(doc.id)}
            />
            <span className="account-document-name" title={doc.filename}>{doc.filename}</span>
            <span>{doc.board_name}</span>
            <span>{doc.card_title}</span>
            <span>{doc.formatted_size}</span>
            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
            <div className="account-document-actions">
              <button
                className="btn-icon"
                onClick={() => onDelete(doc.id)}
                title="Supprimer"
              >
                <Trash width={18} height={18} />
              </button>
            </div>
          </div>
        ))}

        {documents.length === 0 && (
          <div className="no-documents">
            Aucun document trouvé
          </div>
        )}
      </div>
    </div>
  );
};

const ApiTab: React.FC = () => {
  return (
    <div className="api-tab">
      <ApiTokensManagement />
    </div>
  );
};
